import { ArrowRight, Shield, CheckCircle, Globe, Banknote, FileText, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../SEO';
import { pageSEO, faqSchemas } from '../../seoData';
import RelatedServices from '../RelatedServices';

const NriUS = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    { question: "How does FATCA affect US NRIs investing in India?", answer: "FATCA (Foreign Account Tax Compliance Act) requires Indian financial institutions to report US person accounts. This means your Indian investments are reported to the IRS. We help you maintain proper documentation and work with US-compliant fund houses to ensure seamless investing without FATCA issues." },
    { question: "Can US NRIs invest in Indian mutual funds?", answer: "Yes, but with some restrictions. Not all Indian fund houses accept US NRI investments due to FATCA compliance requirements. We work with fund houses that are US NRI-friendly and handle the additional documentation required. Equity, debt, and hybrid fund options are available." },
    { question: "What about double taxation for US NRIs?", answer: "India-US DTAA prevents double taxation. Tax paid in India on capital gains can be claimed as Foreign Tax Credit on your US tax return (Form 1116). We help you structure investments for optimal tax treatment in both countries and provide documentation needed for US tax filing." },
    { question: "What are 401k alternatives for US NRIs in India?", answer: "Indian alternatives include NPS (National Pension System) for retirement with tax benefits, PPF for guaranteed returns, equity mutual funds for growth, and PMS for professional management. We create a comprehensive retirement portfolio in India that complements your US 401k/IRA holdings." },
  ];

  const services = [
    { icon: <Shield className="h-7 w-7 text-white" />, title: "FATCA Compliance", desc: "Complete FATCA-compliant investment management. We work exclusively with fund houses and brokers that accept US NRI investments and handle all reporting requirements." },
    { icon: <Banknote className="h-7 w-7 text-white" />, title: "USD to INR Strategy", desc: "Strategic USD to INR conversion for maximum investment value. We advise on timing, hedging options, and currency-optimized asset allocation for US-based investors." },
    { icon: <FileText className="h-7 w-7 text-white" />, title: "DTAA Tax Optimization", desc: "Maximize India-US DTAA benefits. Claim Foreign Tax Credits (Form 1116) for Indian taxes paid. We provide all documentation needed for your US tax return filing." },
    { icon: <Globe className="h-7 w-7 text-white" />, title: "401k Complementing", desc: "Build an Indian investment portfolio that complements your US 401k, IRA, and Roth IRA. Geographic diversification with India's high-growth economy." },
    { icon: <Video className="h-7 w-7 text-white" />, title: "US Time Zone Support", desc: "Consultations available in EST/PST friendly hours. Weekend and evening video calls to suit your US schedule. IST is +10:30/+13:30 ahead of US time zones." },
    { icon: <CheckCircle className="h-7 w-7 text-white" />, title: "NRE Repatriation", desc: "NRE investments fully repatriable to your US bank account. We handle RBI documentation, TDS certificates, and FBAR reporting support for amounts exceeding $10,000." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.nriUS} faqJsonLd={faqSchemas.nriUS} />

      <section className="relative bg-gradient-to-br from-blue-900 via-red-900 to-blue-900 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              US NRI INVESTMENT SERVICES
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              India Investments for
              <span className="block bg-gradient-to-r from-red-400 to-blue-400 bg-clip-text text-transparent">
                US-Based NRIs
              </span>
            </h1>
            <p className="text-lg text-blue-200 mb-8 leading-relaxed max-w-3xl">
              Navigate FATCA compliance and invest in India with confidence. Rumtek Finvest specializes 
              in US NRI investment advisory since 2009, offering FATCA-compliant fund selection, 
              India-US DTAA optimization, and complete tax documentation support. Build wealth in India 
              while maintaining full compliance with US regulatory requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-white text-blue-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl" data-testid="nri-us-cta">
                Free NRI Consultation <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/nri-financial-planning" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center">
                General NRI Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl font-bold text-blue-900 mb-1">FATCA</div><div className="text-gray-600 text-sm">Fully Compliant</div></div>
            <div><div className="text-3xl font-bold text-blue-900 mb-1">Since 2009</div><div className="text-gray-600 text-sm">Serving US NRIs</div></div>
            <div><div className="text-3xl font-bold text-blue-900 mb-1">DTAA</div><div className="text-gray-600 text-sm">India-US Tax Treaty</div></div>
            <div><div className="text-3xl font-bold text-blue-900 mb-1">US Hours</div><div className="text-gray-600 text-sm">Weekend Consultations</div></div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">Tailored Services for US NRIs</h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">FATCA-compliant investment strategies designed for NRIs across the United States</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-700 to-red-700 rounded-2xl flex items-center justify-center mb-5">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">FATCA & US Compliance Guide</h2>
          <p className="text-lg text-gray-600 text-center mb-10">Essential compliance information for US NRIs investing in India</p>
          <div className="space-y-4">
            {[
              { title: "FATCA Reporting", desc: "Indian financial institutions report US person accounts to the IRS via FATCA. We ensure your investments are with FATCA-compliant institutions and help maintain proper documentation." },
              { title: "FBAR Filing (FinCEN 114)", desc: "If your aggregate Indian accounts exceed $10,000 at any time during the year, you must file FBAR. We provide account statements and documentation to support your FBAR filing." },
              { title: "Foreign Tax Credit (Form 1116)", desc: "Indian capital gains tax and TDS can be claimed as Foreign Tax Credit on your US return. We provide detailed tax certificates and computation documents for your CPA." },
              { title: "PFIC Considerations", desc: "Some Indian mutual funds may be classified as PFICs (Passive Foreign Investment Companies) by the IRS. We help you choose investment structures that minimize PFIC complications." },
            ].map((item, i) => (
              <div key={i} className="bg-red-50 rounded-xl p-6 border border-red-100">
                <h3 className="font-bold text-red-900 text-lg mb-2">{item.title}</h3>
                <p className="text-gray-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">US NRI Investment FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex justify-between items-center">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-2xl text-blue-600 ml-4">{expandedFaq === i ? '−' : '+'}</span>
                </button>
                {expandedFaq === i && <div className="px-6 pb-5"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['nriUK', 'nriUAE', 'mutualFund', 'pms', 'bonds']} />

      <section className="py-16 bg-gradient-to-r from-blue-900 to-red-900">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Start Investing in India from the US</h2>
          <p className="text-lg mb-8 text-blue-200">FATCA-compliant, DTAA-optimized investment advisory. Weekend consultations available.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-blue-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all inline-flex items-center">
              Book Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a href="mailto:rumtekmf@gmail.com" className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-blue-900 transition-all inline-flex items-center">
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NriUS;
